'use client';

import { useState, type SyntheticEvent } from 'react';

import { useRouter } from 'next/navigation';

import { MARKETING_ROUTES } from '@/common/constants';
import { ScanEmailModal } from '@/components/modals/lazy';
import { FormFieldError } from '@/components/modals/shared';
import { useScanDomain } from '@/components/scan-domain-context';
import { Button } from '@/components/ui/button';
import { ClearableInput } from '@/components/ui/clearable-input';
import { useWebMcpTool } from '@/hooks/use-webmcp-tool';
import { SCANNER_COPY } from '@/lib/content';
import { scanBrandTool } from '@/lib/scan-brand-tool';
import { stashScanEmail } from '@/lib/scan-handoff';
import { cn } from '@/lib/utils';
import { URL_REGEX } from '@/lib/validation';

const SCAN_ERROR_ID = 'scan-start-error';

/**
 * Only the field fits here - the scan itself (radar, findings, report) runs on /scan. Taking the
 * email on the way out is what lets /scan start scanning on arrival instead of stopping the
 * visitor for a second form.
 */
export function ScanStartForm({ className }: { className?: string }) {
  const router = useRouter();
  const { domain, setDomain } = useScanDomain();
  const [error, setError] = useState(false);
  const [askingEmail, setAskingEmail] = useState(false);
  const [everAsked, setEverAsked] = useState(false);
  // The scan, offered to a browser agent while the field offering it to a person is on screen.
  useWebMcpTool(scanBrandTool);

  function submit(event: SyntheticEvent) {
    event.preventDefault();
    if (!URL_REGEX.test(domain.trim())) {
      setError(true);
      return;
    }
    setEverAsked(true);
    setAskingEmail(true);
  }

  function start(email: string) {
    setAskingEmail(false);
    // The email rides in session storage rather than the query string: /scan is a shareable,
    // linkable URL and an address in it would leak into history, referrers, and server logs.
    stashScanEmail(email);
    router.push(`${MARKETING_ROUTES.scan}?domain=${encodeURIComponent(domain.trim())}`);
  }

  return (
    <>
      <form onSubmit={submit} noValidate className={cn('max-w-[480px]', className)}>
        <div className="flex flex-col gap-2.5 sm:flex-row">
          <ClearableInput
            value={domain}
            onValueChange={next => {
              setDomain(next);
              setError(false);
            }}
            placeholder={SCANNER_COPY.placeholder}
            aria-label="Your website address"
            clearLabel="Clear the address"
            aria-invalid={error}
            aria-describedby={error ? SCAN_ERROR_ID : undefined}
            // The base Input is transparent, which on the warm paper reads as page rather than
            // field. Only worth it in light: dark keeps the base `dark:bg-input/30`.
            className="h-12 min-h-12 bg-card px-4 font-mono"
          />
          <Button type="submit" className="h-12 px-5 text-base font-semibold">
            {SCANNER_COPY.button}
          </Button>
        </div>
        <FormFieldError
          id={SCAN_ERROR_ID}
          error={error ? SCANNER_COPY.validationError : undefined}
        />
      </form>
      {everAsked && (
        <ScanEmailModal
          open={askingEmail}
          domain={domain.trim()}
          onClose={() => setAskingEmail(false)}
          onConfirm={start}
        />
      )}
    </>
  );
}
